import { Navigate, useParams } from '@tanstack/react-router';
import type { MeOutput } from '@garage/shared';
import { useMe } from '../../shared/auth.js';
import { GP, garagePath } from '../lib/paths.js';
import { Loading } from '../ui/feedback.js';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Garage où ouvrir une notification : celui de l'appartenance destinataire (`?m=<id>`, identifiant opaque
 * ajouté par le serveur à chaque abonnement) s'il est accessible, sinon le garage actif de la session.
 * Le slug ne sert qu'à naviguer : `/g/<slug>` bascule la session, le serveur contrôle les droits.
 */
export function notificationGarageSlug(me: MeOutput, search: string): string | null {
  const m = new URLSearchParams(search).get('m');
  const target = m && UUID.test(m) ? me.memberships.find((x) => x.membershipId === m && x.status === 'active') : undefined;
  return target?.garageSlug ?? me.active?.garageSlug ?? null;
}

function OpenFromNotification({ to, tab }: { to: (interventionId: string | null) => string; tab?: string }) {
  const { interventionId } = useParams({ strict: false }) as { interventionId?: string };
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (!me.data) return <Navigate to="/login" replace />;
  const slug = notificationGarageSlug(me.data, typeof window === 'undefined' ? '' : window.location.search);
  if (!slug) return <Navigate to="/choose-garage" replace />;
  const id = interventionId && UUID.test(interventionId) ? interventionId : null;
  if (id && tab) return <Navigate to={garagePath(slug, to(id))} search={{ onglet: tab } as never} replace />;
  return <Navigate to={garagePath(slug, to(id))} replace />;
}

/**
 * Clic sur une notification push « Nouveau message » (`/ouvrir/conversation/<id>`, service worker) :
 * ouvre la conversation. L'identifiant est opaque ; un dossier d'un autre garage donne « conversation
 * introuvable dans ce garage » (404 de l'API).
 */
export default function OpenConversationPage() {
  return <OpenFromNotification to={(id) => (id ? GP.conversation(id) : GP.messagerie)} />;
}

/**
 * Clic sur une notification push « Décision reçue » (`/ouvrir/travaux-complementaires/<id>`, 02/10/2026) :
 * ouvre l'onglet « Travaux complémentaires » du dossier.
 */
export function OpenComplementsPage() {
  return (
    <OpenFromNotification to={(id) => (id ? GP.intervention(id) : GP.parc)} tab="travaux-complementaires" />
  );
}
